import { lazy, Suspense, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { KeyRound, Loader2 } from 'lucide-react';

import { useLivelloIndietro } from '@/hooks/useLivelloIndietro';
import { env } from '@/env';
import { qgammonAuthHeaders } from '@/lib/qgammon/auth';
import type { MatchStatus } from '@/lib/qgammon/matchStatus';
import type { Player } from '@qlashgeminiz/qgammon-rules';
import { OSCURA } from '@/lib/v3';
import { corpoAccettazione, propostoNellAnteprima } from '@/lib/qgammon/allenamentoInvito';

/**
 * ALLENAMENTO FRA AMICI (cantiere 34): se l'anteprima propone l'allenamento, al posto dell'unico «Accetta» la scelta ESPLICITA «con» / «senza»
 * (chunk lazy dell'allenamento). Solo nel ramo del LETTERALE del flag del coach: questo file e' condiviso e in produzione compila agli stessi byte
 * (V11, P4 = 0). La risposta arriva al corpo dell'accept dalla casella (`corpoAccettazione`), mai da un parametro nuovo.
 */
const SceltaAccettazioneLazy =
  import.meta.env.VITE_FEATURE_BACKGAMMON_COACH_ENABLED === 'true'
    ? lazy(() => import('@/components/backgammon/allenamento/AllenamentoUi').then((m) => ({ default: m.SceltaAccettazione })))
    : null;

interface AcceptInviteSheetProps {
  /**
   * Called with the match + the ACCEPTOR's colour once accept succeeds — e con
   * `status`, che ADR-0114 rende parte della risposta: cio' che nasce e' un
   * APPUNTAMENTO, e il chiamante deve poterlo dire invece di aprire un
   * tabellone che annuncia una partita non iniziata.
   */
  onAccepted: (result: { matchId: string; color: Player; status?: MatchStatus }) => void;
  onClose: () => void;
}

interface InvitePreview {
  creator: { username: string; displayName: string; elo: number };
  matchLength: number;
  expiresAt: string;
  /** Allenamento fra amici (cantiere 34): `"proposto"` se chi invita lo propone. Dato sconosciuto ai tipi 0.23 (V10). */
  allenamento?: unknown;
}

/** 10-char codes are A-Z2-9. Normalise input to that alphabet, max 10 chars. */
function normaliseCode(raw: string): string {
  return raw
    .toUpperCase()
    .replace(/[^A-Z2-9]/g, '')
    .slice(0, 10);
}

/**
 * Accept-by-code sheet: enter a 10-char code → GET /invites/:code preview →
 * POST /invites/:code/accept → hand the {matchId, color} back to the parent,
 * which navigates with `state.perspective` exactly like ranked/practice.
 *
 * Errors map to friendly copy: 404 → "expired, already used, or cancelled by
 * whoever sent it" (the 404 can't tell which); 400
 * cannot_accept_own → a dedicated message; 409 too_many_active_matches → whose
 * concurrent-match cap is full (`who: 'you' | 'creator'`), which matters because
 * only one of the two can act on it. The code is NOT consumed on a 409, so the
 * invite survives for a later attempt.
 */
export function AcceptInviteSheet({ onAccepted, onClose }: AcceptInviteSheetProps) {
  useLivelloIndietro(true, onClose);
  const { t } = useTranslation('backgammon');

  const [code, setCode] = useState('');
  const [preview, setPreview] = useState<InvitePreview | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<
    'expired' | 'own' | 'capYou' | 'capCreator' | 'failed' | null
  >(null);

  async function lookup() {
    if (code.length !== 10) return;
    setLoading(true);
    setError(null);
    setPreview(null);
    try {
      const res = await fetch(`${env.qgammonWsUrl}/invites/${encodeURIComponent(code)}`, {
        headers: await qgammonAuthHeaders(),
      });
      if (res.status === 404) {
        setError('expired');
        return;
      }
      if (!res.ok) throw new Error(`preview failed: ${res.status}`);
      setPreview((await res.json()) as InvitePreview);
    } catch {
      setError('failed');
    } finally {
      setLoading(false);
    }
  }

  async function accept() {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${env.qgammonWsUrl}/invites/${encodeURIComponent(code)}/accept`, {
        method: 'POST',
        headers: await qgammonAuthHeaders(true),
        // Allenamento fra amici: la risposta ESPLICITA di chi accetta per QUESTO codice, se c'e' (casella); senza, `{}` = senza allenamento.
        body: JSON.stringify(import.meta.env.VITE_FEATURE_BACKGAMMON_COACH_ENABLED === 'true' ? corpoAccettazione(code) : {}),
      });
      if (res.status === 400) {
        const body = (await res.json().catch(() => ({}))) as { error?: string };
        setError(body.error === 'cannot_accept_own' ? 'own' : 'failed');
        return;
      }
      if (res.status === 404) {
        setError('expired');
        return;
      }
      if (res.status === 409) {
        const body = (await res.json().catch(() => ({}))) as {
          error?: string;
          who?: string;
        };
        setError(
          body.error !== 'too_many_active_matches'
            ? 'failed'
            : body.who === 'creator'
              ? 'capCreator'
              : 'capYou',
        );
        return;
      }
      if (!res.ok) throw new Error(`accept failed: ${res.status}`);
      const body = (await res.json()) as {
        matchId: string;
        color: Player;
        status?: MatchStatus;
      };
      onAccepted({ matchId: body.matchId, color: body.color, status: body.status });
    } catch {
      setError('failed');
    } finally {
      setLoading(false);
    }
  }

  const ERROR_KEY: Record<NonNullable<typeof error>, string> = {
    expired: 'invite.expired',
    own: 'invite.own',
    capYou: 'invite.atMatchCapYou',
    capCreator: 'invite.atMatchCapCreator',
    failed: 'invite.acceptError',
  };
  const errorText = error ? t(ERROR_KEY[error]) : null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center"
      role="dialog"
      aria-modal="true"
    >
      <button
        type="button"
        aria-label={t('invite.close')}
        onClick={onClose}
        className="absolute inset-0 bg-black/60"
      />
      <div className="relative w-full max-w-md rounded-t-2xl bg-bg-secondary p-4 pb-[max(1rem,env(safe-area-inset-bottom))]">
        <div className="mb-3 flex items-center gap-2">
          <h2 className="flex items-center gap-2 text-base font-bold text-fg">
            <KeyRound className="h-4 w-4 text-brand" />
            {t('invite.haveCode')}
          </h2>
        </div>

        <div className="flex flex-col gap-4 py-2">
          <input
            {...OSCURA}
            type="text"
            inputMode="text"
            autoCapitalize="characters"
            autoCorrect="off"
            spellCheck={false}
            value={code}
            onChange={(e) => {
              setCode(normaliseCode(e.target.value));
              setPreview(null);
              setError(null);
            }}
            placeholder="XXXXXXXXXX"
            maxLength={10}
            className="w-full rounded-xl border border-border bg-bg px-4 py-3 text-center text-xl font-bold tracking-[0.25em] text-fg placeholder:text-fg-muted placeholder:tracking-[0.25em]"
          />

          {errorText && <p className="text-sm text-danger">{errorText}</p>}

          {preview && (
            <p className="text-center text-sm text-fg">
              {t('invite.preview', {
                name: preview.creator.displayName || preview.creator.username,
                elo: preview.creator.elo,
                length: preview.matchLength,
              })}
            </p>
          )}

          {preview ? (
            import.meta.env.VITE_FEATURE_BACKGAMMON_COACH_ENABLED === 'true' && SceltaAccettazioneLazy && propostoNellAnteprima(preview) ? (
              <Suspense fallback={null}>
                <SceltaAccettazioneLazy
                  codice={code}
                  nome={preview.creator.displayName || preview.creator.username}
                  inCorso={loading}
                  onAccetta={() => void accept()}
                />
              </Suspense>
            ) : (
            <button
              type="button"
              onClick={accept}
              disabled={loading}
              className="flex w-full items-center justify-center gap-2 rounded-xl bg-brand py-3 text-sm font-bold text-brand-fg transition-opacity active:opacity-80 disabled:opacity-60"
            >
              {loading && <Loader2 className="h-4 w-4 animate-spin" />}
              {t('invite.accept')}
            </button>
            )
          ) : (
            <button
              type="button"
              onClick={lookup}
              disabled={code.length !== 10 || loading}
              className="flex w-full items-center justify-center gap-2 rounded-xl bg-brand py-3 text-sm font-bold text-brand-fg transition-opacity active:opacity-80 disabled:opacity-60"
            >
              {loading && <Loader2 className="h-4 w-4 animate-spin" />}
              {t('invite.lookup')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
